
import { useState } from "react";
import { Link, useLocation } from "wouter";
import { motion } from "framer-motion";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { 
  Menu, 
  X, 
  Home, 
  Coins, 
  CreditCard, 
  User, 
  Trophy, 
  Gift,
  LogOut
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import bloxBoostLogo from "@assets/BloxboostLogo.png";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [location] = useLocation();
  const { user, logout } = useAuth();
  const { toast } = useToast();

  const navItems = [
    { path: "/", label: "Home", icon: Home },
    { path: "/earn", label: "Earn", icon: Coins },
    { path: "/withdraw", label: "Withdraw", icon: CreditCard },
    { path: "/leaderboard", label: "Leaderboard", icon: Trophy },
    { path: "/promo-codes", label: "Promo Codes", icon: Gift },
  ];

  const handleLogout = async () => {
    try {
      await logout();
      toast({
        title: "Logged out successfully",
        description: "See you next time!",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to logout",
        variant: "destructive",
      });
    }
  };

  return (
    <nav className="fixed top-0 w-full z-50 glass-effect border-b border-border">
      <div className="container mx-auto px-4">
        <div className="relative flex items-center justify-between h-16">
          {/* Logo */}
          <Link href="/" data-testid="link-home">
            <motion.div 
              className="flex items-center space-x-2 cursor-pointer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <img src={bloxBoostLogo} alt="BloxBoost Logo" className="w-8 h-8" />
              <span className="text-xl font-bold gradient-text">BloxBoost</span>
            </motion.div>
          </Link>

          {/* Desktop Navigation (Centered) */}
          <div className="hidden md:flex items-center space-x-8 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            {navItems.map((item) => {
              const isActive = location === item.path;
              return (
                <Link key={item.path} href={item.path} data-testid={`link-${item.label.toLowerCase()}`}>
                  <motion.div
                    className={`text-sm font-medium transition-colors cursor-pointer ${
                      isActive ? "text-primary" : "text-foreground hover:text-primary"
                    }`}
                    whileHover={{ y: -2 }}
                    whileTap={{ y: 0 }}
                  >
                    {item.label}
                  </motion.div>
                </Link>
              );
            })}
          </div>

          {/* User Section */}
          <div className="flex items-center space-x-4">
            {user ? (
              <>
                {/* Token Balance */}
                <motion.div
                  className="hidden md:flex items-center space-x-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20"
                  whileHover={{ scale: 1.05 }}
                  data-testid="token-balance"
                >
                  <Coins className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium">{user.tokenBalance.toLocaleString()}</span>
                  <span className="text-xs text-muted-foreground">tokens</span>
                </motion.div>

                {/* User Avatar */}
                <Link href="/profile">
                  <motion.div
                    className="w-8 h-8 rounded-full bg-muted flex items-center justify-center cursor-pointer hover:bg-muted/80 transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    data-testid="user-avatar"
                  >
                    {user.avatarUrl ? (
                      <img
                        src={user.avatarUrl}
                        alt="Avatar"
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <User className="w-4 h-4" />
                    )}
                  </motion.div>
                </Link>

                {/* Logout Button with Confirmation */}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="hidden md:flex"
                      data-testid="button-logout"
                    >
                      <LogOut className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Are you sure you want to sign out?</AlertDialogTitle>
                      <AlertDialogDescription>
                        You will be returned to the login page.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleLogout}>Sign Out</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            ) : (
              <Link href="/login">
                <Button data-testid="button-login" className="hidden md:flex">Login</Button>
              </Link>
            )}

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={() => setIsOpen(!isOpen)}
              data-testid="button-menu-toggle"
            >
              {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden py-4 border-t border-border"
          >
            <div className="flex flex-col space-y-2">
              {navItems.map((item) => {
                const isActive = location === item.path;
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    href={item.path}
                    data-testid={`link-mobile-${item.label.toLowerCase()}`}
                    onClick={() => setIsOpen(false)}
                  >
                    <motion.div
                      className={`flex items-center space-x-3 px-4 py-2 rounded-lg transition-colors cursor-pointer ${
                        isActive
                          ? "bg-primary/10 text-primary border border-primary/20"
                          : "hover:bg-muted"
                      }`}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="font-medium">{item.label}</span>
                    </motion.div>
                  </Link>
                );
              })}

              {user ? (
                <>
                  {/* Mobile Token Balance */}
                  <div className="flex items-center justify-between px-4 py-2 bg-primary/10 rounded-lg border border-primary/20 mt-4">
                    <div className="flex items-center space-x-2">
                      <Coins className="w-4 h-4 text-primary" />
                      <span className="text-sm font-medium">Balance</span>
                    </div>
                    <span className="text-sm font-bold text-primary" data-testid="mobile-token-balance">
                      {user.tokenBalance.toLocaleString()} tokens
                    </span>
                  </div>

                  {/* Mobile Logout with Confirmation */}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        className="justify-start w-full mt-2"
                        data-testid="button-mobile-logout"
                      >
                        <LogOut className="w-4 h-4 mr-3" />
                        Logout
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Are you sure you want to sign out?</AlertDialogTitle>
                        <AlertDialogDescription>
                          You will be returned to the login page.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setIsOpen(false)}>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={() => {
                            handleLogout();
                            setIsOpen(false);
                          }}
                        >
                          Sign Out
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              ) : (
                <Link href="/login" onClick={() => setIsOpen(false)}>
                  <Button className="w-full justify-center mt-4" data-testid="button-mobile-login">
                    Login
                  </Button>
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
